import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { Card, Button, cn } from '@medikei/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import {
  Users,
  Calendar,
  Star,
  Clock,
  CheckCircle,
  TrendingUp,
  User,
  Radio,
  FileText,
  Video,
  ArrowRight,
  ShieldAlert,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDoctorStatistics } from '@/hooks/doctor/useDoctorStatistics';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import { useUpcomingAppointments } from '@/hooks/scheduling/useAppointments';
import { DoctorOnboardingModal } from '@/components/doctor';
import { useDoctorQueueStore } from '@/stores/useDoctorQueueStore';
import { ActiveConsultationBanner } from '@/components/telemedicine/ActiveConsultationBanner';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
  onClick?: () => void;
}

function MetricCard({ title, value, subtitle, icon: Icon, trend, loading, onClick }: MetricCardProps) {
  if (loading) {
    return (
      <Card className="p-6 bg-card border-border">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-12 w-12 rounded-full" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      onClick={onClick}
      className={cn(
        'p-6 bg-card border-border transition-all rounded-2xl shadow-xs',
        onClick && 'cursor-pointer hover:border-primary/40 hover:shadow-md group'
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground group-hover:text-primary transition-colors">{title}</p>
          <p className="text-3xl font-bold text-foreground mt-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp
                size={14}
                className={trend.isPositive ? 'text-green-500' : 'text-red-500'}
              />
              <span
                className={`text-sm ${trend.isPositive ? 'text-green-500' : 'text-red-500'}`}
              >
                {trend.isPositive ? '+' : ''}{trend.value}%
              </span>
            </div>
          )}
        </div>
        <div className="h-12 w-12 rounded-full bg-primary-500/10 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
          <Icon className="text-primary-500" size={24} />
        </div>
      </div>
    </Card>
  );
}

export function DoctorDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentHour = new Date().getHours();
  const timeOfDay = currentHour < 12 ? 'Bom dia' : currentHour < 18 ? 'Boa tarde' : 'Boa noite';
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  const { data: profile } = useDoctorProfile(user?.id || '');
  const doctorStatus = profile?.status || (user as any)?.doctorStatus || 'PENDING';
  const isApproved = doctorStatus === 'APPROVED';

  const { isOnline } = useDoctorQueueStore();

  const {
    data: stats,
    isLoading: isLoadingStats,
    isError: isErrorStats,
    refetch: refetchStats,
  } = useDoctorStatistics();

  const {
    data: upcomingData,
    isLoading: isLoadingUpcoming,
    isError: isErrorUpcoming,
    refetch: refetchUpcoming,
  } = useUpcomingAppointments(10);

  const hasRealData = !!stats;

  const isAppointmentActive = (scheduledAt: string) => {
    try {
      const aptTime = new Date(scheduledAt).getTime();
      const now = Date.now();
      const diffMinutes = (aptTime - now) / (1000 * 60);
      // Ativo de 15 minutos antes até 60 minutos depois
      return diffMinutes >= -60 && diffMinutes <= 15;
    } catch {
      return false;
    }
  };

  if (isErrorStats && isErrorUpcoming) {
    return (
      <div className="py-12">
        <ErrorRetry
          message="Não foi possível carregar as informações do dashboard médico."
          onRetry={() => {
            refetchStats();
            refetchUpcoming();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Banner de Reconexão para Teleconsulta Ativa */}
      <ActiveConsultationBanner role="DOCTOR" />

      {/* Alert Banner for pending / under review status */}
      {!isApproved && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 text-amber-900 dark:text-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300 shrink-0">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">
                {doctorStatus === 'REJECTED'
                  ? 'Atenção: Seu cadastro precisa de correções documentais'
                  : 'Cadastro Médico em Análise & Habilitação'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Para ter acesso às funcionalidades de atendimento, emissão de receitas digitais e plantão express,
                complete o envio dos documentos comprobatórios obrigatórios (CRM, Diploma e RG/CNH).
              </p>
            </div>
          </div>
          <Button
            onClick={() => setIsOnboardingOpen(true)}
            className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs sm:text-sm h-10 px-5 rounded-xl shadow-md shadow-amber-600/20 cursor-pointer"
          >
            Completar Cadastro & Documentos
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {timeOfDay}, {user?.name?.split(' ')[0] || 'Doutor'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Aqui está o resumo da sua atividade hoje.
          </p>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="grid gap-4 sm:grid-cols-3">
        <button
          onClick={() => {
            if (!isApproved) {
              setIsOnboardingOpen(true);
            } else {
              navigate('/medico/fila');
            }
          }}
          className={cn(
            'flex items-center gap-4 p-4 rounded-xl border transition-all text-left group shadow-sm cursor-pointer',
            isOnline
              ? 'border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/20 hover:bg-emerald-50/70 dark:hover:bg-emerald-950/30'
              : 'border-border bg-card hover:bg-muted/40'
          )}
        >
          <div
            className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform',
              isOnline
                ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30'
                : 'bg-emerald-500/10 text-emerald-600'
            )}
          >
            <Radio className={cn('h-6 w-6', isOnline && 'animate-pulse')} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground text-sm truncate">
                {isOnline ? 'Você está de Plantão' : 'Central de Plantão Express'}
              </h3>
              {isOnline && (
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping shrink-0" />
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {isOnline ? 'Acompanhar fila em tempo real (Ao vivo)' : 'Entrar na fila e receber chamadas'}
            </p>
          </div>
          <ArrowRight
            className={cn(
              'h-5 w-5 group-hover:translate-x-1 transition-transform shrink-0',
              isOnline ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
            )}
          />
        </button>

        <button
          onClick={() => {
            if (!isApproved) {
              setIsOnboardingOpen(true);
            } else {
              navigate('/medico/receitas/novo');
            }
          }}
          className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:bg-muted/40 transition-colors text-left group shadow-sm cursor-pointer"
        >
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center group-hover:scale-105 transition-transform">
            <FileText className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">Nova Receita</h3>
            <p className="text-xs text-muted-foreground">Emitir prescrição rápida</p>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
        </button>

        <button
          onClick={() => navigate('/medico/agenda')}
          className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:bg-muted/40 transition-colors text-left group shadow-sm cursor-pointer"
        >
          <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 flex items-center justify-center group-hover:scale-105 transition-transform">
            <Calendar className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">Minha Agenda</h3>
            <p className="text-xs text-muted-foreground">Gerenciar horários e consultas</p>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Cards de Métricas */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Pacientes Atendidos"
          value={hasRealData ? stats.totalPatients : '-'}
          subtitle="Total de pacientes"
          icon={Users}
          loading={isLoadingStats}
          onClick={() => navigate('/medico/pacientes')}
        />
        <MetricCard
          title="Consultas Realizadas"
          value={hasRealData ? stats.totalAppointments : '-'}
          subtitle="Total de consultas"
          icon={Calendar}
          loading={isLoadingStats}
          onClick={() => navigate('/medico/agenda')}
        />
        <MetricCard
          title="Avaliação Média"
          value={hasRealData ? stats.averageRating.toFixed(1) : '-'}
          subtitle="Total de avaliações"
          icon={Star}
          loading={isLoadingStats}
          onClick={() => navigate('/medico/perfil')}
        />
        <MetricCard
          title="Taxa de Conclusão"
          value={hasRealData && stats.completionRate !== null ? `${stats.completionRate}%` : '-'}
          subtitle="Consultas concluídas"
          icon={CheckCircle}
          loading={isLoadingStats}
          onClick={() => navigate('/medico/historico')}
        />
      </div>

      {/* Seções Principais: Próximas Consultas e Resumo */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6 bg-card border-border flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <Clock className="text-primary-500 h-5 w-5" />
                <h3 className="font-semibold text-foreground">Próximas Consultas</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/medico/agenda')}
                className="text-xs text-primary-500 hover:text-primary-600 hover:bg-primary-500/10"
              >
                Ver Agenda
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>

            <div className="space-y-3">
              {isLoadingUpcoming ? (
                <>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                    </div>
                    <Skeleton className="h-8 w-20" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                    </div>
                    <Skeleton className="h-8 w-20" />
                  </div>
                </>
              ) : isErrorUpcoming ? (
                <div className="py-4">
                  <ErrorRetry
                    message="Erro ao carregar próximas consultas"
                    onRetry={() => refetchUpcoming()}
                  />
                </div>
              ) : upcomingData?.appointments && upcomingData.appointments.length > 0 ? (
                upcomingData.appointments.slice(0, 5).map((apt) => {
                  const active = isAppointmentActive(apt.scheduledAt);
                  return (
                    <div
                      key={apt.id}
                      onClick={() => navigate(`/medico/agenda?appointmentId=${apt.id}`)}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-muted/40 hover:bg-muted/70 rounded-xl border border-border gap-3 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                          <User className="text-primary-500 h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">
                            {apt.patient?.name || 'Paciente'}
                          </p>
                          <p className="text-xs text-muted-foreground">{apt.reason || 'Consulta'}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                        <div className="text-left sm:text-right">
                          <p className="text-sm font-bold text-foreground">
                            {format(new Date(apt.scheduledAt), 'HH:mm', { locale: ptBR })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(apt.scheduledAt), 'dd/MM', { locale: ptBR })}
                          </p>
                        </div>

                        {active ? (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-8 text-xs font-semibold shadow-sm cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/medico/telemedicine/session/${apt.id}`);
                            }}
                          >
                            <Video className="h-3.5 w-3.5" />
                            Iniciar Consulta
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-8 cursor-pointer rounded-lg border-border bg-card text-foreground hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/medico/agenda?appointmentId=${apt.id}`);
                            }}
                          >
                            Ver Detalhes
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Nenhuma consulta agendada para os próximos horários.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-primary"
                    onClick={() => navigate('/medico/agenda')}
                  >
                    Abrir agenda completa
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Resumo */}
        <Card className="p-6 bg-card border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Resumo Geral</h3>
            {isErrorStats && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetchStats()}
                className="text-xs text-muted-foreground"
              >
                Recarregar
              </Button>
            )}
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Total de pacientes</span>
              <span className="font-medium text-foreground">{hasRealData ? stats.totalPatients : '-'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Avaliações recebidas</span>
              <span className="font-medium text-foreground">{hasRealData ? stats.totalRatings : '-'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Tempo médio de resposta</span>
              <span className="font-medium text-foreground">
                {hasRealData && stats.responseTime ? `${stats.responseTime} min` : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Taxa de conclusão</span>
              <span className="font-medium text-foreground">
                {hasRealData && stats.completionRate !== null ? `${stats.completionRate}%` : '-'}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Onboarding & Document Compliance Modal */}
      <DoctorOnboardingModal
        open={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        doctor={profile as any}
        onRefreshProfile={() => {
          refetchStats();
          refetchUpcoming();
        }}
      />
    </div>
  );
}
